"use client";
import NoImage from "@/assets/images/no-image.webp";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import CloseIcon from "@/icons/CloseIcon";
import SearchIcon from "@/icons/SearchIcon";

import { getRecentSearches, saveSearchQuery } from "@/lib/searchHistory";
import { staticSlug } from "@/lib/staticSearchQuery";
import { decodeHTML } from "@/lib/utils";
import { useGetSuggestionsQuery } from "@/redux/apiSlices/supportApi";
import { AddNewBreadcrum } from "@/redux/slices/breadcrumSlice";
import { useQueryParams } from "@/utils/useQueryParams";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import DynamicImage from "../DynamicImages/DynamicImage";
import { Badge } from "../ui/badge";

const GlobalSearch = () => {
  const defaultSlug = "products";
  const { keyword = "" } = useQueryParams();
  const [inputValue, setInputValue] = useState(keyword);
  const [inputFocused, setInputFocused] = useState(false);
  const [hoveredKeyword, setHoveredKeyword] = useState("");
  const searchRef = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const textQuery = searchParams?.get("keyword");
  const [value] = useDebounce(inputValue, 100);
  const [isMobile, setIsMobile] = useState(false);

  const { data, isFetching } = useGetSuggestionsQuery(
    { keyword: value, autoCorrect: false },
    { skip: !value }
  );
  const {
    suggestions = [],
    category = [],
    productSuggestions = [],
  } = data?.data || {};

  const isEmptyResponse =
    !data?.data || (suggestions.length === 0 && category.length === 0);
  const { data: autoCorrectData } = useGetSuggestionsQuery(
    { keyword: value, autoCorrect: true },
    { skip: !value || !isEmptyResponse }
  );
  const correctedKeyword = autoCorrectData?.correctedKeyword;
  
  const updatedBreadcrumbs = (breadcrumb) => {
    dispatch(AddNewBreadcrum([breadcrumb]));
  };

  const handleSearch = (value = inputValue, slug) => {
    if (!value?.trim()) {
      return;
    }

    const normalizedQuery = value.trim().toLowerCase();
    const resolvedSlug =
      slug ||
      (isFetching ? defaultSlug : suggestions?.[0]?.slug || defaultSlug);

    let isStaticPageMatch = staticSlug[normalizedQuery]
      ? normalizedQuery
      : Object.keys(staticSlug).find((key) =>
          normalizedQuery.includes(key.toLowerCase())
        ) || null;

    const href = isStaticPageMatch
      ? `/${staticSlug[isStaticPageMatch]}`
      : `/${resolvedSlug}?keyword=${encodeURIComponent(value)}`;

    saveSearchQuery({ text: value, slug: resolvedSlug });
    setInputFocused(false);
    updatedBreadcrumbs({
      name: isStaticPageMatch || "Search Results",
      url: href,
    });
    router.push(href);
  };

  // Example usage for Enter key press
  const onEnterPress = () => {
    handleSearch();
  };

  // Example usage for selection
  const handleSelect = (value, slug) => {
    setInputValue(value);
    handleSearch(value, slug);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && inputValue.length > 0) {
      onEnterPress();
    }
  };

  const onSearchChange = (e) => {
    setInputValue(e.target.value);
    if (hoveredKeyword) {
      setHoveredKeyword("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setInputFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Now it's safe to access `window`
    const checkMobile = () => setIsMobile(window.innerWidth < 450);

    checkMobile(); // Run once on mount
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const recentSearches = getRecentSearches()?.filter(({ text }) => text) || [];

  const customSuggestions = useMemo(() => {
    if (!inputValue) {
      // Mark all API suggestions as 'api'
      return suggestions.map((suggestion) => ({
        type: "api",
        ...suggestion,
      }));
    }

    // Filter recent searches and mark them as 'recent'
    const filteredRecentSearches = recentSearches
      .filter(
        ({ text }) =>
          text && text.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map((sug) => ({ type: "recent", ...sug }));

    // Mark API suggestions as 'api'
    const apiSuggestions = suggestions.map((suggestion) => ({
      type: "api",
      ...suggestion,
    }));

    // Combine both, ensuring recent searches appear first
    return [...filteredRecentSearches, ...apiSuggestions];
  }, [inputValue, recentSearches, suggestions]);

  const handleOnClearAll = () => {
    localStorage.removeItem("recentSearches");
    setInputFocused(false);
  };

  useEffect(() => {
    // Clear input only if the 'text' query param does not exist
    if (!textQuery) {
      setInputValue("");
    }
  }, [pathname, textQuery]);

  return (
    <>
      <div ref={searchRef} className="relative flex-1">
        <div className="relative w-full overflow-hidden">
          <input
            type="text"
            placeholder={isMobile ? "" : "Search for Product, Brand, and More"}
            className=" marquee-input m-0 lg:text-[13px] lg:rounded-[7px] w-full  py-2 border border-gray-300 rounded-[10px] px-4 pr-[80px] focus:outline-none mob:pl-[12px]"
            value={inputValue}
            onChange={onSearchChange}
            onFocus={() => setInputFocused(true)}
            onKeyDown={handleKeyDown}
          />
          {isMobile && !inputValue.trim() && (
            <>
              <span className="marquee-placeholder lg:text-[13px] lg:rounded-[7px]">
                Search for Product, Brand, and More
              </span>
              <span className=" absolute left-0 bg-white h-full w-[12px] border-r-0 border lg:rounded-l-[7px] border-gray-300 rounded-l-[10px]  "></span>
            </>
          )}
          <span
            className={` ${
              inputValue.trim() ? "bg-white  block " : "bg-[#f5f5f5]  hidden"
            } absolute px-3  flex items-center  top-[3px] right-[43px] cursor-pointer h-[calc(100%-6px)]   lg:rounded-r-[7px] border-gray-300 rounded-r-[10px] `}
            onClick={() => setInputValue("")}
          >
            <CloseIcon fill="#0B223C" className=" w-[12px] h-[12px] " />
          </span>
          <span
            className={` ${
              !inputValue.trim()
                ? "bg-white border-l-0 "
                : "bg-[#f5f5f5] border-l"
            } absolute px-3  flex items-center top-0 right-0 cursor-pointer h-full  border lg:rounded-r-[7px] border-gray-300 rounded-r-[10px] `}
            onClick={onEnterPress}
          >
            <SearchIcon className=" w-[15px] h-[15px] " />
          </span>
        </div>

        {!isFetching &&
          inputValue &&
          inputFocused && (
            <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[596px] slg:min-w-0">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {isEmptyResponse && correctedKeyword && (
                      <div className="px-8 py-1 flex gap-2 flex-wrap">
                        <p className="text-sm font-medium">Did you mean?</p>
                        <Badge className="px-2 bg-white text-black hover:bg-white border border-gray-300">
                          <span
                            onClick={() => {
                              setInputValue(correctedKeyword);
                            }}
                            className="cursor-pointer"
                          >
                            {correctedKeyword}
                          </span>
                        </Badge>{" "}
                      </div>
                    )}
                    {!isEmptyResponse && customSuggestions.length > 0 && (
                      <div className=" grid grid-cols-2 gap-2 md:grid-cols-1 ">
                        <div className=" flex-1">
                          {customSuggestions.map(
                            ({ type, text: value, slug }, index) => (
                              <div
                                key={index}
                                onMouseOver={() => setHoveredKeyword(value)}
                              >
                                <CommandItem
                                  onSelect={() => handleSelect(value, slug)}
                                  className="cursor-pointer"
                                >
                                  {type == "api" ? (
                                    <svg
                                      className="w-4 h-4 opacity-50 mr-3"
                                      viewBox="0 0 24 24"
                                      focusable="false"
                                      aria-hidden="true"
                                      data-testid="SearchIcon"
                                    >
                                      <path d="M15.375 16.966A9.46 9.46 0 0 1 9.5 19 9.5 9.5 0 1 1 19 9.5a9.46 9.46 0 0 1-2.034 5.875l7.012 7.012-1.59 1.591-7.013-7.012ZM16.75 9.5a7.25 7.25 0 1 0-14.5 0 7.25 7.25 0 0 0 14.5 0Z"></path>
                                    </svg>
                                  ) : (
                                    <svg
                                      className=" w-4 h-4 opacity-50 mr-3"
                                      viewBox="0 0 24 24"
                                      focusable="false"
                                      aria-hidden="true"
                                      data-testid="ClockIcon"
                                    >
                                      <path d="M13.125 11.46V5.25h-2.25v7.29l5.05 4.041 1.406-1.757-4.206-3.365Z"></path>
                                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0ZM2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Z"></path>
                                    </svg>
                                  )}

                                  {value}
                                </CommandItem>
                                {index ===
                                  customSuggestions.findIndex(
                                    (s) => s.type === "api"
                                  ) && (
                                  <>
                                    {category.map(
                                      ({ value, slug: catSlug }, catIndex) => {
                                        const mainHref = `/${
                                          catSlug || defaultSlug
                                        }?keyword=${suggestions?.[0]?.text}`;

                                        return (
                                          <div
                                            key={catIndex}
                                            className="pl-6 w-full"
                                          >
                                            <CommandItem className="text-gray-500 text-xs py-0">
                                              <Link
                                                href={mainHref}
                                                onClick={() => {
                                                  setInputFocused(false);
                                                  updatedBreadcrumbs({
                                                    name: value,
                                                    url: mainHref,
                                                  });
                                                }}
                                                className="w-full flex items-center"
                                              >
                                                <span className="text-3xl">
                                                  ⤷
                                                </span>{" "}
                                                <span className="text-sm px-2">
                                                  {value}
                                                </span>
                                              </Link>
                                            </CommandItem>
                                          </div>
                                        );
                                      }
                                    )}
                                  </>
                                )}
                              </div>
                            )
                          )}
                        </div>
                        <div className="flex overflow-x-scroll flex-col gap-4 border-l pl-1 md:hidden">
                          <ProductSuggestionContainer
                            initialSuggestions={productSuggestions}
                            hoveredKeyword={hoveredKeyword}
                          />
                        </div>
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
          )}
        {!inputValue && inputFocused && recentSearches.length > 0 && (
          <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[596px] slg:min-w-0">
            <Command>
              <CommandList>
                <CommandGroup>
                  <div className=" flex-1">
                    <div className="flex justify-between items-center p-[12px_10px] text-[15px]">
                      <p className="mr-2">Recent Searches</p>
                      <p
                        className="underline cursor-pointer"
                        onClick={handleOnClearAll}
                      >
                        Clear All
                      </p>
                    </div>
                    {recentSearches.map(({ text: value, slug }, index) => (
                      <div
                        key={index}
                        onMouseOver={() => setHoveredKeyword(value)}
                      >
                        <CommandItem
                          onSelect={() => handleSelect(value, slug)}
                          className="cursor-pointer"
                        >
                          <svg
                            className=" w-4 h-4 opacity-50 mr-3"
                            viewBox="0 0 24 24"
                            focusable="false"
                            aria-hidden="true"
                            data-testid="ClockIcon"
                          >
                            <path d="M13.125 11.46V5.25h-2.25v7.29l5.05 4.041 1.406-1.757-4.206-3.365Z"></path>
                            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0ZM2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Z"></path>
                          </svg>
                          {value}
                        </CommandItem>
                      </div>
                    ))}
                  </div>
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>
      {inputValue && inputFocused && (suggestions.length > 0 || correctedKeyword) && (
        <div className=" h-searchFocus fixed top-[79px] right-0 left-0 bottom-0 bg-black/80 lg:top-[99px]" />
      )}
    </>
  );
};

export default GlobalSearch;

const ProductSuggestionContainer = ({ initialSuggestions, hoveredKeyword }) => {
  const { data } = useGetSuggestionsQuery(
    {
      keyword: hoveredKeyword,
      productOnly: true,
    },
    { skip: !hoveredKeyword }
  );
  console.log("ProductSuggestionContainer data:", data);

  const { productSuggestions = [] } = data?.data || {};

  const suggestions = hoveredKeyword ? productSuggestions : initialSuggestions;

  return suggestions.map(({ productName, pdpSeoUrl, productImages }, index) => (
    <Link
      key={index}
      href={`/${pdpSeoUrl}`}
      className="px-1 flex gap-2 items-center"
      onClick={() => setInputFocused(false)}
    >
      <DynamicImage
        src={productImages[0]?.thumbnail || NoImage.src}
        width={100}
        height={100}
        className="object-contain min-h-[70px] max-h-[70px]"
      />
      <p className="text-[14px] two-line-dots leading-2 text-black2  max-w-[230px] hover:underline">
        {decodeHTML(productName)}
      </p>
    </Link>
  ));
};